import { Button, Combobox, makeStyles, Option, OptionOnSelectData, SelectionEvents, Tooltip } from '@fluentui/react-components';
import { AddCircle20Filled, AddCircle20Regular, bundleIcon, SubtractCircle20Filled, SubtractCircle20Regular } from '@fluentui/react-icons';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormView } from '../../contexts/useFormView';
import { createFitTypeZoomSettings, createPercentageZoomSettings, FormSettings } from '../../utils/FormZoomSettings';
import { IFormInteractionHandler } from '../tables/common-individual-report-table/IFormInteractionHandler';

const AddCircleBundle = bundleIcon(AddCircle20Filled, AddCircle20Regular);
const SubtractCircleBundle = bundleIcon(SubtractCircle20Filled, SubtractCircle20Regular);

export interface IFormZoomControlsProps {
    formInteractionHandler: IFormInteractionHandler;
}

const useStyles = makeStyles({
    root: {
        borderTopWidth: 0,
        borderRightWidth: 0,
        borderBottomWidth: 0,
        borderLeftWidth: 0,
        borderTopStyle: 'none',
        borderRightStyle: 'none',
        borderBottomStyle: 'none',
        borderLeftStyle: 'none',
        minWidth: '2.2rem',
        '& input': {
            width: '2.2rem',
            paddingLeft: 0,
        },
    },
    stack: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: '5px',
    },
});

export const FormZoomControls: React.FC<IFormZoomControlsProps> = ({ formInteractionHandler }) => {
    const { t } = useTranslation();

    const styles = useStyles();

    const [selectedKey, setSelectedKey] = React.useState<string>('fitToWidth');
    const { formSettings, hasError, isLoading, isFormZoomReady, updateFormSettings } = useFormView();
    const fitToPageOption = { key: 'fitToPage', text: t('fitPage') };
    const fitToWidthOption = { key: 'fitToWidth', text: t('fitWidth') };
    const fitAutoOption = { key: 'auto', text: t('automatic') };
    const options = [
        { key: '0.1', text: '10%' },
        { key: '0.25', text: '25%' },
        { key: '0.5', text: '50%' },
        { key: '0.75', text: '75%' },
        { key: '1', text: '100%' },
        { key: '1.25', text: '125%' },
        { key: '1.5', text: '150%' },
        { key: '2', text: '200%' },
        { key: '4', text: '400%' },
        fitToPageOption,
        fitToWidthOption,
        fitAutoOption,
    ];
    const zoomPercentage = `${Math.floor(formSettings.currentZoom)}%`;
    const canDisplayZoom = isFormZoomReady && !isLoading;
    const zoomDisplayValue = canDisplayZoom ? zoomPercentage : '';

    const isMinimumZoomFactor = selectedKey === '0.1';
    const isMaximumZoomFactor = selectedKey === '4';

    const persistZoomSettings = (settings: FormSettings) => {
        updateFormSettings(settings);
    };

    const zoomOut = () => {
        const newZoomFactor = formInteractionHandler.zoomOut();
        setSelectedKey(`${newZoomFactor}`);
        persistZoomSettings(createPercentageZoomSettings(Math.round(newZoomFactor * 100)));
    };

    const zoomIn = () => {
        const newZoomFactor = formInteractionHandler.zoomIn();
        setSelectedKey(`${newZoomFactor}`);
        persistZoomSettings(createPercentageZoomSettings(Math.round(newZoomFactor * 100)));
    };

    useEffect(() => {
        if (formSettings.zoomFitType === 'fitToPage') {
            setSelectedKey(fitToPageOption.key);
            return;
        }

        if (formSettings.zoomFitType === 'fitToWidth') {
            setSelectedKey(fitToWidthOption.key);
            return;
        }

        if (formSettings.zoomFitType === 'fitToAuto') {
            setSelectedKey(fitAutoOption.key);
            return;
        }

        const selectedIndex = options.findIndex((option) => option.text === zoomPercentage);
        setSelectedKey(selectedIndex !== -1 ? options[selectedIndex].key : '');
    }, [formSettings.currentZoom, formSettings.zoomFitType, fitAutoOption.key, fitToPageOption.key, fitToWidthOption.key, zoomPercentage]);

    const selectZoomFactor = (_: SelectionEvents, data: OptionOnSelectData): void => {
        if (data && data.optionValue) {
            const key: string = data.optionValue;

            if (key === fitToPageOption.key) {
                setSelectedKey(key);
                persistZoomSettings(createFitTypeZoomSettings('fitToPage', formSettings.currentZoom));
                formInteractionHandler.fitToPage();
                return;
            }

            if (key === fitToWidthOption.key) {
                setSelectedKey(key);
                persistZoomSettings(createFitTypeZoomSettings('fitToWidth', formSettings.currentZoom));
                formInteractionHandler.fitToWidth();
                return;
            }

            if (key === fitAutoOption.key) {
                setSelectedKey(key);
                persistZoomSettings(createFitTypeZoomSettings('fitToAuto', formSettings.currentZoom));
                formInteractionHandler.fitToAuto();
                return;
            }

            setSelectedKey(key);
            const zoomPercentage = parseFloat(key) * 100;
            persistZoomSettings(createPercentageZoomSettings(zoomPercentage));
            formInteractionHandler.zoomTo(zoomPercentage);
        }
    };

    return (
        <div className={styles.stack}>
            <Tooltip content={t('zoomOut').toString()} relationship='description' appearance='inverted' withArrow positioning='above'>
                <Button
                    appearance='subtle'
                    icon={<SubtractCircleBundle />}
                    aria-label={t('zoomOut').toString()}
                    disabled={isMinimumZoomFactor || hasError}
                    disabledFocusable={isMinimumZoomFactor || hasError}
                    onClick={zoomOut}
                />
            </Tooltip>

            <Tooltip content={t('zoomIn').toString()} relationship='description' appearance='inverted' withArrow positioning='above'>
                <Button
                    appearance='subtle'
                    icon={<AddCircleBundle />}
                    aria-label={t('zoomIn').toString()}
                    disabled={isMaximumZoomFactor || hasError}
                    disabledFocusable={isMaximumZoomFactor || hasError}
                    onClick={zoomIn}
                />
            </Tooltip>

            <Combobox
                data-testid='zoom-combobox'
                value={zoomDisplayValue}
                disabled={hasError || !canDisplayZoom}
                selectedOptions={selectedKey ? [selectedKey] : []}
                className={styles.root}
                onOptionSelect={selectZoomFactor}
            >
                {options.map((option) => (
                    <Option key={option.key} value={option.key} text={option.text}>
                        {option.text}
                    </Option>
                ))}
            </Combobox>
        </div>
    );
};
